import { WbMultiSelect, WbSelect } from '@agilelab/plugin-wb-platform';
import { MenuItem, Popper, PopperProps } from '@material-ui/core';
import {
  enumOptionsIndexForValue,
  enumOptionsValueForIndex,
  UiSchema,
  WidgetProps,
} from '@rjsf/utils';
import { ChangeEvent, FocusEvent, useCallback } from 'react';
import { isHidden, UI_STYLE_SAFE_KEY } from '../../utils';

const MAX_CHIPS = 3;

const WidePopper = (props: PopperProps) => {
  return <Popper {...props} style={{ width: 850 }} placement="bottom-start" />;
};

function extractCustomProperties(uiSchema: UiSchema) {
  const customProps: any = {};
  const widePopper =
    uiSchema && uiSchema['ui:popper'] && uiSchema['ui:popper'] === 'wide';
  const customStyle = uiSchema && uiSchema[UI_STYLE_SAFE_KEY];
  if (widePopper) {
    customProps.PopperComponent = WidePopper;
  }
  if (customStyle) {
    customProps.InputProps = {
      style: customStyle,
    };
  }
  return customProps;
}

export const SelectWidget = ({
  schema,
  id,
  options,
  required,
  disabled,
  readonly,
  value,
  multiple = false,
  onChange,
  onBlur,
  onFocus,
  uiSchema,
  placeholder,
  rawErrors = [],
  error = false,
}: WidgetProps) => {
  const { enumOptions, enumDisabled, emptyValue: optEmptyVal } = options;
  const emptyValue = multiple ? [] : '';
  const customProps = uiSchema ? extractCustomProperties(uiSchema) : {};

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLSelectElement>) => {
      const newValue = event.target.value;
      return onFocus(
        id,
        enumOptionsValueForIndex(newValue, enumOptions, optEmptyVal),
      );
    },
    [onFocus, id, enumOptions, optEmptyVal],
  );

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLSelectElement>) => {
      const newValue = event.target.value;
      return onBlur(
        id,
        enumOptionsValueForIndex(newValue, enumOptions, optEmptyVal),
      );
    },
    [onBlur, id, enumOptions, optEmptyVal],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const newValue = event.target.value;

      const val = enumOptionsValueForIndex(newValue, enumOptions, optEmptyVal);

      onChange(val);
    },
    [onChange, enumOptions, optEmptyVal],
  );

  const selectedIndexes = enumOptionsIndexForValue(
    value,
    enumOptions,
    multiple,
  );

  const showPlaceholderOption = !multiple && schema.default === undefined;

  if (multiple) {
    return (
      <WbMultiSelect
        id={id}
        name={id}
        label={schema.title}
        style={{ display: isHidden(uiSchema) ? 'none' : undefined }}
        value={
          typeof selectedIndexes === 'undefined' ? emptyValue : selectedIndexes
        }
        required={required}
        disabled={disabled || readonly}
        error={rawErrors.length > 0 || error}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        limitSelectedDisplay={MAX_CHIPS}
        expandChipsVertically={false}
        options={enumOptions?.map((_, i) => String(i))}
        getOptionLabel={val =>
          enumOptionsValueForIndex(val, enumOptions, optEmptyVal)
        }
        getDisabled={val =>
          enumDisabled &&
          enumOptionsIndexForValue(val, enumOptions) !== undefined
        }
        {...customProps}
      />
    );
  }
  return (
    <WbSelect
      id={id}
      name={id}
      label={schema.title}
      style={{ display: isHidden(uiSchema) ? 'none' : undefined }}
      value={
        typeof selectedIndexes === 'undefined' ? emptyValue : selectedIndexes
      }
      required={required}
      disabled={
        disabled ||
        readonly ||
        ((enumOptions as any[]).length === 1 &&
          typeof selectedIndexes === 'string' &&
          required &&
          !!schema.default &&
          selectedIndexes === schema.default)
      }
      error={rawErrors.length > 0}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      {...customProps}
      PopperComponent={WidePopper}
    >
      {showPlaceholderOption && (
        // this is for allow unselecting the value, when possible. This character is a non-breaking space, to make the option visible even if empty
        <MenuItem value={undefined}>{placeholder || '\u00A0'}</MenuItem>
      )}
      {(enumOptions as any).map(
        ({ value: optionValue, label: optionLabel }: any, i: number) => {
          const disabledItem: any =
            enumDisabled && (enumDisabled as any).indexOf(optionValue) !== -1;
          return (
            <MenuItem key={i} value={i} disabled={disabledItem}>
              {optionLabel}
            </MenuItem>
          );
        },
      )}
    </WbSelect>
  );
};
